import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle, Shield, Lock, CreditCard, Smartphone, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';

const API = process.env.REACT_APP_BACKEND_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Componente Form Pagamento CARTA
const CardPaymentForm = ({ checkInId, token, amount, patientName, onPaymentSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError.message);
        toast.error(submitError.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const confirmRes = await axios.post(
          `${API}/api/waiting-room/confirm-stripe-payment/${checkInId}`,
          null,
          { params: { payment_intent_id: paymentIntent.id, token } }
        );

        if (confirmRes.data.success) {
          toast.success('Pagamento completato con successo!');
          onPaymentSuccess();
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Errore nel pagamento');
      toast.error('Errore nel pagamento. Riprova.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 text-blue-800 font-medium mb-2">
          <CreditCard className="w-5 h-5" />
          Inserisci i dati della carta
        </div>
        <p className="text-blue-600 text-sm">Visa, Mastercard, American Express</p>
      </div>

      <PaymentElement 
        options={{
          layout: 'tabs',
          paymentMethodOrder: ['card'],
          defaultValues: {
            billingDetails: { name: patientName }
          }
        }}
      />
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className={`w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${
          processing 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-green-600 hover:bg-green-700 hover:shadow-lg'
        }`}
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Elaborazione in corso...
          </>
        ) : (
          <>
            <Lock className="w-5 h-5" />
            Paga €{amount?.toFixed(2)} con Carta
          </>
        )}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="w-full py-3 text-gray-600 hover:text-gray-800 text-sm"
      >
        ← Torna alle opzioni di pagamento
      </button>

      <div className="flex items-center justify-center gap-2 text-gray-400 text-xs">
        <Shield className="w-4 h-4" />
        <span>Pagamento sicuro crittografato</span>
      </div>
    </form>
  );
};

// Componente Form Pagamento WALLET (Apple Pay, Google Pay, PayPal, Link)
const WalletPaymentForm = ({ checkInId, token, amount, patientName, onPaymentSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError.message);
        toast.error(submitError.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const confirmRes = await axios.post(
          `${API}/api/waiting-room/confirm-stripe-payment/${checkInId}`,
          null,
          { params: { payment_intent_id: paymentIntent.id, token } }
        );

        if (confirmRes.data.success) {
          toast.success('Pagamento completato con successo!');
          onPaymentSuccess();
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Errore nel pagamento');
      toast.error('Errore nel pagamento. Riprova.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 text-purple-800 font-medium mb-2">
          <Wallet className="w-5 h-5" />
          Scegli il metodo di pagamento
        </div>
        <p className="text-purple-600 text-sm">Apple Pay, Google Pay, PayPal, Link o Carta</p>
      </div>

      <PaymentElement 
        options={{
          layout: 'accordion',
          defaultCollapsed: false,
          radios: true,
          spacedAccordionItems: true,
          defaultValues: {
            billingDetails: { name: patientName }
          }
        }}
      />
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className={`w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${
          processing 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-purple-600 hover:bg-purple-700 hover:shadow-lg'
        }`}
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Elaborazione in corso...
          </>
        ) : (
          <>
            <Lock className="w-5 h-5" />
            Paga €{amount?.toFixed(2)}
          </>
        )}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="w-full py-3 text-gray-600 hover:text-gray-800 text-sm"
      >
        ← Torna alle opzioni di pagamento
      </button>

      <div className="flex items-center justify-center gap-2 text-gray-400 text-xs">
        <Shield className="w-4 h-4" />
        <span>Pagamento sicuro con Stripe</span>
      </div>
    </form>
  );
};

// Componente principale
const CheckInPayment = () => {
  const { checkInId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [checkIn, setCheckIn] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null); // null, 'card', 'wallet'

  useEffect(() => {
    loadCheckInData();
  }, [checkInId]);

  const loadCheckInData = async () => {
    try {
      const res = await axios.get(`${API}/api/waiting-room/check-in-status/${checkInId}?token=${token}`);
      
      if (res.data) {
        setCheckIn(res.data);
        
        if (res.data.payment_completed) {
          setPaymentComplete(true);
          setLoading(false);
          return;
        }
        
        // Crea PaymentIntent
        try {
          const intentRes = await axios.post(
            `${API}/api/waiting-room/create-payment-intent/${checkInId}`,
            null,
            { params: { token } }
          );
          
          if (intentRes.data.already_paid) {
            setPaymentComplete(true);
          } else {
            setClientSecret(intentRes.data.client_secret);
          }
        } catch (intentErr) {
          console.error('Error creating payment intent:', intentErr);
          setError('Errore inizializzazione pagamento. Riprova più tardi.');
        }
      } else {
        setError('Check-in non trovato');
      }
    } catch (err) {
      console.error(err);
      setError('Errore caricamento dati. Link non valido o scaduto.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2A9FCB] to-[#1E6E99] flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2A9FCB] to-[#1E6E99] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <AlertTriangle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Errore</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-[#2A9FCB] text-white px-6 py-2 rounded-lg hover:bg-[#1E88B5]"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  // Payment complete state
  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-green-700 mb-2">Pagamento Completato!</h1>
          <p className="text-gray-600 mb-6">
            Grazie {checkIn?.patient_name?.split(' ')[0]}! Sei ora in sala d'attesa.
          </p>
          
          <div className="bg-green-50 rounded-xl p-4 mb-6">
            <p className="text-green-800 font-medium">🪑 Sei in Sala d'Attesa</p>
            <p className="text-green-600 text-sm mt-1">
              Riceverai SMS ed Email quando il medico ti chiamerà
            </p>
          </div>
          
          <p className="text-sm text-gray-500">
            Puoi chiudere questa pagina
          </p>
        </div>
      </div>
    );
  }

  const stripeOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#2A9FCB',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        borderRadius: '8px',
      },
      rules: {
        '.Input': {
          border: '1px solid #d1d5db',
          padding: '12px',
        },
        '.Input:focus': {
          border: '2px solid #2A9FCB',
          boxShadow: '0 0 0 1px #2A9FCB',
        },
      }
    },
    locale: 'it'
  };

  const amount = checkIn?.payment_amount || 80.00;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2A9FCB] to-[#1E6E99] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-[#2A9FCB] text-white p-6 text-center">
          <img src="/logo-primevision-full.png" alt="PRIME VISION" className="h-8 w-auto object-contain mx-auto mb-2 brightness-0 invert" />
          <p className="text-white/80 text-sm">Centro di Eccellenza Oculistico</p>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-center text-gray-600 mb-2">Benvenuto/a</p>
          <h2 className="text-center text-xl font-bold text-gray-800 mb-6">
            {checkIn?.patient_name}
          </h2>

          {/* Importo */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6 text-center">
            <p className="text-sm text-gray-500 mb-1">Importo da pagare</p>
            <p className="text-4xl font-bold text-[#2A9FCB]">
              €{amount.toFixed(2)}
            </p>
            <p className="text-sm text-gray-500 mt-2">Visita Oculistica</p>
          </div>

          {/* Scelta metodo pagamento */}
          {!paymentMode && clientSecret && (
            <div className="space-y-4">
              <p className="text-center text-gray-600 font-medium mb-4">
                Come vuoi pagare?
              </p>
              
              {/* Opzione 1: Paga subito con carta */}
              <button
                onClick={() => setPaymentMode('card')}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white p-5 rounded-xl hover:shadow-lg transition-all flex items-center gap-4"
              >
                <div className="bg-white/20 p-3 rounded-lg">
                  <CreditCard className="w-8 h-8" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-lg">Paga subito con carta</p>
                  <p className="text-white/80 text-sm">Visa, Mastercard, American Express</p>
                </div>
                <span className="text-2xl font-bold">€{amount.toFixed(2)}</span>
              </button>

              {/* Opzione 2: Altri metodi (Apple Pay, Google Pay, PayPal) */}
              <button
                onClick={() => setPaymentMode('wallet')}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white p-5 rounded-xl hover:shadow-lg transition-all flex items-center gap-4"
              >
                <div className="bg-white/20 p-3 rounded-lg">
                  <Smartphone className="w-8 h-8" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-lg">Link di pagamento diretto</p>
                  <p className="text-white/80 text-sm">Apple Pay, Google Pay, PayPal, Carta</p>
                </div>
                <div className="flex gap-1">
                  <span className="bg-white/20 px-2 py-1 rounded text-xs"></span>
                  <span className="bg-white/20 px-2 py-1 rounded text-xs">GPay</span>
                </div>
              </button>

              {/* Info sicurezza */}
              <div className="flex items-center justify-center gap-2 text-gray-400 text-xs mt-4">
                <Shield className="w-4 h-4" />
                <span>Pagamento sicuro con Stripe</span>
                <Lock className="w-4 h-4 ml-2" />
                <span>SSL Crittografato</span>
              </div>
            </div>
          )}

          {/* Form pagamento carta */}
          {paymentMode === 'card' && clientSecret && (
            <Elements stripe={stripePromise} options={stripeOptions}>
              <CardPaymentForm 
                checkInId={checkInId}
                token={token}
                amount={amount}
                patientName={checkIn?.patient_name}
                onPaymentSuccess={() => setPaymentComplete(true)}
                onBack={() => setPaymentMode(null)}
              />
            </Elements>
          )}

          {/* Form pagamento wallet */}
          {paymentMode === 'wallet' && clientSecret && (
            <Elements stripe={stripePromise} options={stripeOptions}>
              <WalletPaymentForm 
                checkInId={checkInId}
                token={token}
                amount={amount}
                patientName={checkIn?.patient_name}
                onPaymentSuccess={() => setPaymentComplete(true)}
                onBack={() => setPaymentMode(null)}
              />
            </Elements>
          )}

          {/* Loading Stripe */}
          {!clientSecret && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#2A9FCB] mb-2" />
              <p className="text-gray-500">Inizializzazione pagamento...</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="h-5 opacity-50" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckInPayment;
